import { ref, reactive, computed, toRefs } from 'vue'
import { isArray } from '@/common/js/utils'
import moment from 'moment'
import { REPORT_MATERIAL_ANALYSIS } from '@/views/download-center/data/const.js'
import { getMaterialAnalysisData, initDownloadCenterTask, updateMaterialAnalysisRemark } from '@/api/report.model'

// Constants
export const DATE_TYPE = {
  DAY: 1,
  WEEK: 2,
  MONTH: 3,
  TOTAL: 4
}

export const SUB_MATERIAL = '1'

export const DIMENSION_OPTIONS = [
  { label: '素材', key: 'dimMaterial' },
  { label: '媒体', key: 'dimMedia', tableField: 'mediaName', width: 90 },
  { label: '素材类型', key: 'dimImgType', tableField: 'type', width: 80 },
  { label: '数据游戏组', key: 'dimDataGame', tableField: 'dataGameName', width: 110, overflow: true }
]

export const MATERIAL_DETAIL_COLUMN_KEYS = new Set([
  'coverUrl',
  'imageId',
  'imageName',
  'type',
  'designerName',
  'plannerName',
  'uploadTime',
  'lauchTime',
  'imageStyle2LabelNames',
  'aiLabelNames',
  'videoLabelNames',
  'gameNames',
  'imageOrientation',
  'remark'
])

export const useDateFilter = (getData) => {
  const state = reactive({
    dateType: 4,
    datetime: [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
    weekRange: [],
    monthRange: []
  })

  const disabledDate = (date) => {
    return date && date.valueOf() > Date.now()
  }

  const weekOptions = {
    firstDayOfWeek: 1,
    cellClassName: (date) => {
      if (date && date.valueOf() > Date.now() - 1000 * 60 * 60 * 24) return 'empty-date'
      return ''
    },
    disabledDate: (date) => {
      const isMonday = new Date().getDay() === 1
      if (isMonday) {
        return date && date.valueOf() > Date.now() - 1000 * 60 * 60 * 24
      }
      return date && date.valueOf() > Date.now() + (7 - new Date().getDay()) * 1000 * 60 * 60 * 24
    }
  }

  const handlerDateType = (val) => {
    switch (val) {
      case DATE_TYPE.WEEK: {
        // 默认周一到周天
        let monday = moment().weekday(0)
        let lastday = moment().weekday(6)
        // v4.22.1 如果今天是星期一，取上周一到上周天
        if (moment().day() === 1) {
          monday = moment().weekday(-7)
          lastday = moment().weekday(-1)
        }
        state.weekRange = [monday.format('YYYY-MM-DD'), lastday.format('YYYY-MM-DD')]
        break
      }

      case DATE_TYPE.MONTH: {
        // v4.22.1 例如今天是1月1日，则选中的是上月
        // 例如今天是1月2日，则选中的是本月
        let month = moment().format('YYYY-MM-01')
        const today = moment().format('YYYY-MM-DD')
        if (today.endsWith('-01')) {
          month = moment().subtract(1, 'month').format('YYYY-MM-DD')
        }
        state.monthRange = [month, month]
        break
      }
      default:
        break
    }

    if (getData) getData()
  }

  const onDateRangeChange = () => {
    if (getData) getData()
  }

  const getDateParams = () => {
    const [beginTime, endTime] = state.datetime || []
    let dates = { beginTime, endTime }

    if (state.dateType === DATE_TYPE.WEEK) {
      const [b, e] = state.weekRange.map((d) => moment(d).format('YYYY-MM-DD'))
      dates = { beginTime: b, endTime: e }
    }

    if (state.dateType === DATE_TYPE.MONTH) {
      const [b, e] = state.monthRange.map((d) => moment(d).format('YYYY-MM-DD'))
      dates = { beginTime: b, endTime: e }
    }
    return {
      beginTime: moment(dates.beginTime).startOf('day').valueOf(),
      endTime: moment(dates.endTime).endOf(state.dateType === DATE_TYPE.MONTH ? 'month' : 'day').valueOf(),
      dateType: state.dateType
    }
  }

  return {
    ...toRefs(state),
    disabledDate,
    weekOptions,
    handlerDateType,
    onDateRangeChange,
    getDateParams
  }
}

export const useDimensions = () => {
  const dimensionKeys = DIMENSION_OPTIONS.map(({ key }) => key)
  const dimensionParamKeys = new Set(['dimOrder', 'dimSelectCount', ...dimensionKeys])
  const dimensionState = reactive({
    selectedDimensions: ['dimMaterial'],
    appliedDimensions: ['dimMaterial']
  })

  const normalizeDimensions = (dimensions) => {
    const normalizedDimensions = []
    const safeDimensions = Array.isArray(dimensions) ? dimensions : []
    safeDimensions.forEach((key) => {
      if (dimensionKeys.includes(key) && !normalizedDimensions.includes(key)) normalizedDimensions.push(key)
    })
    return normalizedDimensions
  }

  const commitDimensions = () => {
    dimensionState.appliedDimensions = [...dimensionState.selectedDimensions]
  }

  const getDimensionParams = (dimensions = dimensionState.appliedDimensions) => {
    const normalizedDimensions = normalizeDimensions(dimensions)
    const params = {
      dimOrder: normalizedDimensions,
      dimSelectCount: normalizedDimensions.length
    }
    DIMENSION_OPTIONS.forEach(({ key }) => {
      params[key] = normalizedDimensions.includes(key) ? 1 : undefined
    })
    return params
  }

  const restoreSelectedDimensions = (params) => {
    if (!params || typeof params !== 'object') return
    const hasDimensionParams = Array.from(dimensionParamKeys).some((key) => Object.prototype.hasOwnProperty.call(params, key))
    if (!hasDimensionParams) return

    const orderedDimensions = normalizeDimensions(params.dimOrder)
    const flaggedDimensions = normalizeDimensions(DIMENSION_OPTIONS.filter(({ key }) => params[key]).map(({ key }) => key))
    const restoredDimensions = orderedDimensions.length ? orderedDimensions : flaggedDimensions
    dimensionState.selectedDimensions = restoredDimensions.length ? restoredDimensions : [dimensionKeys[0]]
  }

  const omitDimensionParams = (params) => {
    return Object.entries(params || {}).reduce((filterParams, [key, value]) => {
      if (!dimensionParamKeys.has(key)) filterParams[key] = value
      return filterParams
    }, {})
  }

  return {
    ...toRefs(dimensionState),
    commitDimensions,
    getDimensionParams,
    restoreSelectedDimensions,
    omitDimensionParams
  }
}

export const useColumns = (appliedDimensions) => {
  const tableCustomColsRef = ref(null)
  const state = reactive({
    tableColumns: [],
    toggleTableColLoading: false
  })

  const tableCol = computed(() => {
    const dimensions = appliedDimensions.value
    const hasMaterialDimension = dimensions.includes('dimMaterial')
    const hasMaterialTypeDimension = dimensions.includes('dimImgType')
    const customCols = state.tableColumns.filter((col) => col.checked)
    const dimensionCols = dimensions
      .map((key) => DIMENSION_OPTIONS.find((item) => item.key === key))
      .filter((item) => item?.tableField)
      .map((item) => ({
        title: item.label,
        key: item.tableField,
        width: item.width,
        overflow: item.overflow,
        fixed: true,
        checked: true
      }))
    const result = [...dimensionCols, ...customCols]
      .filter((col, index, cols) => cols.findIndex((item) => item.key === col.key) === index)
      // type 既是素材明细字段，也是素材类型维度的分组字段；dimImgType 生效时必须保留该列。
      .filter(
        (col) =>
          hasMaterialDimension ||
          !MATERIAL_DETAIL_COLUMN_KEYS.has(col.key) ||
          (hasMaterialTypeDimension && col.key === 'type')
      )

    if (hasMaterialDimension) {
      result.splice(Math.min(3, result.length), 0, {
        title: '操作',
        key: 'control',
        fixed: true,
        width: 80
      })
    }

    return result
  })

  const onCustomColumnChange = async (columns) => {
    state.toggleTableColLoading = true
    await new Promise((resolve) => {
      setTimeout(resolve, 100)
    })
    state.tableColumns = columns
    state.toggleTableColLoading = false
  }

  const headerDragend = (n, o, column) => {
    tableCustomColsRef.value?.updateTableColWidth(n, column)
  }

  return {
    tableCustomColsRef,
    ...toRefs(state),
    tableCol,
    onCustomColumnChange,
    headerDragend
  }
}

export const useTableData = (buildQueryParams, proxy, router) => {
  const state = reactive({
    tableHeight: 330,
    tableList: [],
    summaries: {},
    tableLoading: false,
    pageIndex: 1,
    pageSize: 20,
    total: 0,
    sort: undefined,
    order: undefined,
    existParams: {}
  })

  const getImageOrientation = ({ width, height }) => {
    if (!width || !height) return ''
    if (width > height) return '横版'
    if (width < height) return '竖版'
    if (width === height) return '方形'
  }

  const getData = (commitQuery = false) => {
    const queryParams = buildQueryParams(commitQuery)
    const params = {
      ...queryParams,
      pageId: state.pageIndex,
      pageSize: state.pageSize
    }
    state.existParams = params

    if (state.tableLoading) return
    state.tableLoading = true
    // 每次加载表格数据时，清空表格数据否则组件内部素材显示的el-popover会定位不准
    state.tableList = []
    getMaterialAnalysisData(params)
      .then((data) => {
        if (!data) return
        const { list, page_info, sum_data } = data
        if (isArray(list) && page_info) {
          const { total_number } = page_info
          state.total = total_number || 0
          state.tableList = list.map((row) => {
            if (row.coverUrl && row.coverUrl.indexOf(',') > -1) {
              const imgs = row.coverUrl.split(',')
              row.double = imgs.length === 2
              row.multiple = imgs.length > 2
              row.cover = imgs[0]
            } else row.cover = row.coverUrl
            row.video = Boolean(row.imgType === 2)
            const imageOrientation = getImageOrientation(row)

            return {
              ...row,
              lauchTime: row.lauchTime ? moment(row.lauchTime).format('YYYY-MM-DD') : '',
              uploadTime: row.uploadTime ? moment(row.uploadTime).format('YYYY-MM-DD HH:mm:ss') : '',
              imageOrientation
            }
          })
          state.summaries = sum_data || {}
        }
      })
      .finally(() => {
        state.tableLoading = false
      })
  }

  const onQueryAction = () => {
    state.pageIndex = 1
    getData(true)
  }

  const refreshData = () => {
    state.pageIndex = 1
    getData()
  }

  const changePageIndex = (index) => {
    state.pageIndex = index
    getData()
  }

  const changePageSize = (size) => {
    state.pageSize = size
    state.pageIndex = 1
    refreshData()
  }

  const sortTable = (params) => {
    const { prop, order: sortOrder } = params
    let orderText = ''
    if (sortOrder === 'ascending') orderText = 'asc'
    if (sortOrder === 'descending') orderText = 'desc'

    state.sort = prop
    state.order = orderText
    refreshData()
  }

  const handleMaterialComments = (row) => {
    router.push({
      name: 'materialAnalysisComments',
      query: {
        imageName: encodeURIComponent(row.imageName ? row.imageName.split('-')[0] : row.imageName)
      }
    })
  }

  function buildRowQueryParams({ dimImageDetail, dateType, row }) {
    const { imageId, imageDetailId } = row
    const { beginTime, endTime, platforms, mediaIds } = state.existParams
    return {
      id: dimImageDetail === SUB_MATERIAL ? imageDetailId : imageId,
      dimImageDetail,
      beginTime: moment(beginTime).format('YYYY-MM-DD'),
      endTime: moment(endTime).format('YYYY-MM-DD'),
      platforms: platforms?.length ? platforms.join(',') : '',
      mediaIds: mediaIds?.length ? mediaIds.join(',') : ''
    }
  }

  const handleTableRowAdDailyDetail = (info) => {
    router.push({
      name: 'materialAnalysisLifeCycle',
      query: { ...buildRowQueryParams(info) }
    })
  }

  const handleTableRowAdDetail = (info) => {
    const { dataGameIds, dataGroupIds, launchOpIdsStr } = state.existParams
    router.push({
      name: 'materialAnalysisDetail',
      query: {
        ...buildRowQueryParams(info),
        dataGameIds: dataGameIds?.length ? dataGameIds.join(',') : '',
        dataGroupIds: dataGroupIds?.length ? dataGroupIds.join(',') : '',
        launchOpIdsStr
      }
    })
  }

  return {
    ...toRefs(state),
    getData,
    onQueryAction,
    refreshData,
    changePageIndex,
    changePageSize,
    sortTable,
    handleTableRowAdDetail,
    handleTableRowAdDailyDetail,
    handleMaterialComments
  }
}

export const useRemark = (tableList, proxy) => {
  const remarkState = reactive({
    selectedRows: [],
    remarkDialogVisible: false,
    remark: '',
    remarkSaving: false
  })

  const onSelectionChange = (rows) => {
    remarkState.selectedRows = rows
  }

  const openRemarkDialog = () => {
    if (!remarkState.selectedRows.length) return
    const remarks = [...new Set(remarkState.selectedRows.map((row) => row.remark || ''))]
    remarkState.remark = remarks.length === 1 ? remarks[0] : ''
    remarkState.remarkDialogVisible = true
  }

  const saveRemark = async () => {
    const imageIds = [...new Set(remarkState.selectedRows.map((row) => row.imageId).filter(Boolean))]
    if (!imageIds.length) return

    remarkState.remarkSaving = true
    try {
      const data = await updateMaterialAnalysisRemark({ imageIds: JSON.stringify(imageIds), remark: remarkState.remark || '' })
      if (!data) return
      tableList.value = tableList.value.map((row) =>
        imageIds.includes(row.imageId) ? { ...row, remark: remarkState.remark || '' } : row
      )
      remarkState.remarkDialogVisible = false
      proxy.$Message.success('备注保存成功')
    } finally {
      remarkState.remarkSaving = false
    }
  }

  return {
    ...toRefs(remarkState),
    onSelectionChange,
    openRemarkDialog,
    saveRemark
  }
}

export const useExport = (existParams, tableColumns, filterRef, proxy) => {
  const state = reactive({
    exportLoading: false
  })

  const onDownloadDataAction = (dimImageDetail) => {
    const { pageId, pageSize, ...restSearchParams } = existParams.value
    const searchParams = {}
    for (const key in restSearchParams) {
      const value = restSearchParams[key]
      const hasValue = value !== undefined && value !== null && value !== '' && (!Array.isArray(value) || value.length > 0)
      if (hasValue && value !== -1) {
        searchParams[key] = value
      }
    }
    const exportColumnOrderMap = { dateDesc: '日期' }
    tableColumns.value.forEach((col) => {
      if (col.checked && !col.exclude_export) {
        // 子素材、母素材这个字段单独处理
        if (col.key === 'imageId') {
          const prop = dimImageDetail === SUB_MATERIAL ? 'imageDetailId' : 'imageId'
          exportColumnOrderMap[prop] = col.title
        } else {
          exportColumnOrderMap[col.key] = col.title
        }
      }
    })
    state.exportLoading = true

    const conditionText = filterRef.value?.getTransferedTaskQueryConditions(searchParams, REPORT_MATERIAL_ANALYSIS)
    initDownloadCenterTask({
      pageType: REPORT_MATERIAL_ANALYSIS,
      queryCondition: { ...searchParams, exportColumnOrderMap, conditionText }
    })
      .then((data) => {
        if (data.msg) {
          proxy.$Message.success(data.msg)
          proxy.$store.commit('setDownloadBadgeActive', 1)
        }
      })
      .finally(() => {
        state.exportLoading = false
      })
  }

  return {
    ...toRefs(state),
    onDownloadDataAction
  }
}
